import React, { useMemo } from 'react';
import Icon from '../Icon/Icon';
import type { PaginationInfo } from '../../../types';
import styles from './Pagination.module.css';

interface PaginationProps {
  pagination: PaginationInfo;
  loading?: boolean;
  onPageChange: (page: number) => void;
  maxVisible?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  loading = false,
  onPageChange,
  maxVisible = 5,
}) => {
  const { page, totalPages, hasNextPage, hasPreviousPage } = pagination;

  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [page, totalPages, maxVisible]);

  return (
    <nav className={styles.pagination} aria-label="Paginação">
      <button
        className={styles.btn}
        onClick={() => onPageChange(page - 1)}
        disabled={!hasPreviousPage || loading}
        aria-label="Página anterior"
      >
        <Icon name="chevron_left" size={18} />
      </button>

      {pageNumbers[0] > 1 && (
        <>
          <button
            className={`${styles.btn} ${page === 1 ? styles.active : ''}`}
            onClick={() => onPageChange(1)}
            disabled={loading}
          >
            1
          </button>
          {pageNumbers[0] > 2 && <span className={styles.ellipsis}>…</span>}
        </>
      )}

      {pageNumbers.map((num) => (
        <button
          key={num}
          className={`${styles.btn} ${num === page ? styles.active : ''}`}
          onClick={() => onPageChange(num)}
          disabled={loading}
          aria-current={num === page ? 'page' : undefined}
        >
          {num}
        </button>
      ))}

      {pageNumbers[pageNumbers.length - 1] < totalPages && (
        <>
          {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
            <span className={styles.ellipsis}>…</span>
          )}
          <button
            className={`${styles.btn} ${page === totalPages ? styles.active : ''}`}
            onClick={() => onPageChange(totalPages)}
            disabled={loading}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        className={styles.btn}
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNextPage || loading}
        aria-label="Próxima página"
      >
        <Icon name="chevron_right" size={18} />
      </button>

      <span className={styles.info}>
        Página {page} de {totalPages}
      </span>
    </nav>
  );
};

export default Pagination;
